/**
 * The calendar section: Agora's own screens against `@wtfalch/calendar`'s
 * `CalendarClient`, drawn to match the design canvas
 * (`Calendar.dc.html`, `Event.dc.html`, `Find-a-time.dc.html`) rather than
 * reusing that package's own `react` entry -- Agora draws every screen
 * itself now (see the shell's own docblock).
 *
 * `location.eventId` opens `Event`; otherwise `Week` draws the seven days
 * around `location.date` (today, when absent). "New event" is the address
 * `/calendar/new[/<date>]` (`location.creating`), so the Calendar tile and
 * Back leave the form the way they leave any other screen.
 */
import type {
  CalendarClient,
  CalendarEvent,
  Calendar as CalendarModel,
} from '@wtfalch/calendar/calendar';
import { CalendarError } from '@wtfalch/calendar/calendar';
import { Panes } from '@wtfalch/design';
import type { MailClient } from '@wtfalch/email/mailbox';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { RoomCallBinding } from '../calls/roomCalls.ts';
import type { CalendarLocation } from '../routes.ts';
import { useWeekNav } from '../shortcuts.tsx';
import './calendar.css';
import { eventCallUrl } from '../calls/eventCalls.ts';
import { CalendarSearchResults } from './CalendarSearchResults.tsx';
import { Event } from './Event.tsx';
import { EventCall } from './EventCall.tsx';
import { FindTime } from './FindTime.tsx';
import { startOfWeek, weekDays, weekRange } from './layout.ts';
import type { Identity } from './participants.ts';
import { Week } from './Week.tsx';

export type CalendarSectionProps = {
  client: CalendarClient;
  /** For `Event`'s own "Propose a new time" (agora#286): a `COUNTER` rides
   *  out as ordinary mail (`@wtfalch/email/imip`/`mailbox`), so the section
   *  needs the same mail client `Mail` and `Today` already hold, not a
   *  second one of its own. */
  mailClient: MailClient;
  /** For the full-results view's own rows (agora#269's
   *  `CalendarSearchResults`), which -- like every combined-search row
   *  (`search/calendar.ts`'s `routeFor`) -- carries an `AgoraRoute` rather
   *  than a bare `CalendarLocation`. */
  agoraId: string;
  location: CalendarLocation;
  /** The signed-in person's own name, from the mail SDK's identities --
   *  `CalendarIdentity` carries no name of its own (agora#1, item 15's own
   *  Finding). Threaded down to `FindTime`, whose own row needs it. */
  meName: string | null;
  onNavigate: (location: CalendarLocation) => void;
  onError?: (error: CalendarError) => void;
  /** Threaded straight through to `Event` -- see its own docblock for why
   *  this is optional. */
  calls?: RoomCallBinding;
  /** Opens an address in the app; an event's "where it came from" card uses it. */
  go?: (path: string) => void;
};

function dateParam(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseDateParam(value: string | null): Date {
  if (value) {
    const [y, m, d] = value.split('-').map(Number);
    if (y && m && d) return new Date(y, m - 1, d);
  }
  return new Date();
}

export function Calendar({
  client,
  mailClient,
  agoraId,
  location,
  meName,
  onNavigate,
  onError,
  calls,
  go,
}: CalendarSectionProps) {
  const anchor = useMemo(() => parseDateParam(location.date), [location.date]);
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const days = useMemo(() => weekDays(weekStart), [weekStart]);

  const [identity, setIdentity] = useState<Identity | null>(null);
  const [calendars, setCalendars] = useState<readonly CalendarModel[]>([]);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [events, setEvents] = useState<readonly CalendarEvent[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const creating = location.creating === true;

  const reportError = useCallback(
    (err: unknown) => {
      if (err instanceof CalendarError) {
        onError?.(err);
        setLoadError(err.message);
      } else {
        setLoadError('Something went wrong reaching the calendar.');
      }
    },
    [onError],
  );

  useEffect(() => {
    let live = true;
    client
      .identity()
      .then((id) => {
        if (live) setIdentity(id);
      })
      .catch(reportError);
    client
      .listCalendars()
      .then((list) => {
        if (live) setCalendars(list);
      })
      .catch(reportError);
    return () => {
      live = false;
    };
  }, [client, reportError]);

  useEffect(() => {
    let live = true;
    setLoadError(null);
    setLoading(true);
    client
      .listEvents(weekRange(weekStart))
      .then((list) => {
        if (live) setEvents(list);
      })
      .catch(reportError)
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [client, weekStart, reportError]);

  useEffect(() => {
    const sub = client.subscribe((changed) => {
      if (changed.CalendarEvent) {
        client.listEvents(weekRange(weekStart)).then(setEvents).catch(reportError);
      }
      if (changed.Calendar) {
        client.listCalendars().then(setCalendars).catch(reportError);
      }
    });
    return () => sub.close();
  }, [client, weekStart, reportError]);

  const visibleEvents = useMemo(
    () => events.filter((e) => !hidden.has(e.calendarId)),
    [events, hidden],
  );

  const toggleCalendar = useCallback((id: string, visible: boolean) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const goWeek = useCallback(
    (d: Date | null) => {
      onNavigate({ date: d ? dateParam(d) : null, eventId: null });
    },
    [onNavigate],
  );

  // The event's own day, never `location.date`: that is `null` on the
  // default week, and an address with an event id but no date drops the id.
  const openEvent = useCallback(
    (id: string, start?: string) => {
      const own = start ?? events.find((e) => e.id === id)?.start;
      const date = own ? dateParam(new Date(own)) : dateParam(anchor);
      onNavigate({ date, eventId: id });
    },
    [onNavigate, events, anchor],
  );

  const closeEvent = useCallback(() => {
    onNavigate({ date: location.date, eventId: null });
  }, [onNavigate, location.date]);

  const prevWeek = useCallback(
    () => goWeek(new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() - 7)),
    [goWeek, weekStart],
  );
  const nextWeek = useCallback(
    () => goWeek(new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 7)),
    [goWeek, weekStart],
  );

  // Disabled once `Event`, `FindTime` or the full-results view are drawn
  // instead of `Week` below -- `j`/`k` are Mail's and Chat's own list keys
  // there (an open event's body, a search box), not this section's.
  useWeekNav({
    enabled: !creating && !location.eventId && location.query === undefined,
    onPrev: prevWeek,
    onNext: nextWeek,
  });

  // The full-results view (agora#269): reached at
  // `/h/<agoraId>/calendar/search/<query>`, what Enter opens from the
  // header search when nothing is highlighted. `parse` (`routes.ts`) never
  // sets both this and `eventId`, so the order against the check below
  // does not matter in practice.
  if (location.query !== undefined) {
    return (
      <div className="calendar contents">
        <CalendarSearchResults
          client={client}
          agoraId={agoraId}
          query={location.query}
          onOpen={(row) => {
            if (row.route.section === 'calendar') onNavigate(row.route.location);
          }}
        />
      </div>
    );
  }

  // `/calendar/call/<uid>`: with calls on, the call itself. With calls off
  // this falls through to the week view and touches nothing.
  if (location.callUid !== undefined && calls) {
    return (
      <Panes className="calendar flex-1 min-w-0">
        <EventCall calls={calls} uid={location.callUid} onLeft={closeEvent} />
      </Panes>
    );
  }

  if (location.eventId) {
    return (
      <Panes className="calendar flex-1 min-w-0">
        <Event
          client={client}
          mailClient={mailClient}
          identity={identity}
          calendars={calendars}
          agoraId={agoraId}
          eventId={location.eventId}
          onClose={closeEvent}
          onError={reportError}
          calls={calls}
          go={go}
        />
      </Panes>
    );
  }

  if (creating) {
    return (
      <Panes className="calendar flex-1 min-w-0">
        <FindTime
          client={client}
          identity={identity}
          meName={meName}
          calendars={calendars}
          initialDate={anchor}
          onCancel={() => onNavigate({ date: location.date, eventId: null })}
          onCreated={(event) => openEvent(event.id, event.start)}
          onError={reportError}
          callUrl={calls ? (uid) => eventCallUrl(window.location.origin, agoraId, uid) : undefined}
          {...(calls ? { calls } : {})}
        />
      </Panes>
    );
  }

  return (
    <Panes className="calendar flex-1 min-w-0">
      <Week
        client={client}
        days={days}
        events={visibleEvents}
        calendars={calendars}
        hidden={hidden}
        onToggleCalendar={toggleCalendar}
        onPrevWeek={prevWeek}
        onNextWeek={nextWeek}
        onToday={() => goWeek(null)}
        onNewEvent={() => onNavigate({ date: location.date, eventId: null, creating: true })}
        onOpenEvent={openEvent}
        identity={identity}
        error={loadError}
        loading={loading}
      />
    </Panes>
  );
}
